import type { AchievementCondition, AchievementDef, RunStatKey } from '../../data/schema';
import type { RunSummary } from '../run';

/** Compteurs cumulés sur toutes les runs du joueur. */
export interface MetaCounters { runs: number; victories: number; kills: number; kamasEarned: number; bossKills: number; deaths: number; roomsCleared: number; playtimeMs: number; }

export function emptyCounters(): MetaCounters {
  return { runs: 0, victories: 0, kills: 0, kamasEarned: 0, bossKills: 0, deaths: 0, roomsCleared: 0, playtimeMs: 0 };
}

/** Ajoute une run terminée aux compteurs (copie, l'argument n'est pas modifié). */
export function applyRunToCounters(c: MetaCounters, s: RunSummary): MetaCounters {
  const victory = s.outcome === 'victory';
  return {
    runs: c.runs + 1,
    victories: c.victories + (victory ? 1 : 0),
    kills: c.kills + s.kills,
    kamasEarned: c.kamasEarned + s.stats.kamasEarned,
    bossKills: c.bossKills + (victory ? 1 : 0),
    deaths: c.deaths + (victory ? 0 : 1),
    roomsCleared: c.roomsCleared + s.roomsCleared,
    playtimeMs: c.playtimeMs + s.durationMs,
  };
}

/** Valeur d'une statistique : champs du résumé, sinon compteurs de la run. */
function statOf(s: RunSummary, key: RunStatKey): number {
  switch (key) {
    case 'kills': return s.kills;
    case 'kamas': return s.kamas;
    case 'level': return s.level;
    case 'roomsCleared': return s.roomsCleared;
    case 'durationMs': return s.durationMs;
    default: return s.stats[key];
  }
}

export function conditionMet(cond: AchievementCondition, s: RunSummary, counters: MetaCounters): boolean {
  const victory = s.outcome === 'victory';
  switch (cond.type) {
    case 'outcome': return victory;
    case 'stat': return cond.op === '>=' ? statOf(s, cond.stat) >= cond.value : statOf(s, cond.stat) <= cond.value;
    case 'bossKillMs': return s.stats.bossKillMs !== null && s.stats.bossKillMs <= cond.max;
    case 'cumulative': return counters[cond.counter] >= cond.value;
    case 'noDamageRooms': return s.stats.roomsWithoutDamage >= cond.value;
    case 'victoryWith': return victory && s.talentsOwned <= cond.talentsMax;
    case 'dailyVictory': return s.dailySeed && victory;
    case 'killsOf': return (s.stats.killsByDefId[cond.defId] ?? 0) >= cond.value;
    case 'all': return cond.conditions.every((c) => conditionMet(c, s, counters));
  }
}

/** Ids des succès nouvellement obtenus par cette run (hors `already`), dans l'ordre des définitions. */
export function detectAchievements(defs: Record<string, AchievementDef>, s: RunSummary, counters: MetaCounters, already: readonly string[]): string[] {
  const have = new Set(already);
  return Object.values(defs).filter((d) => !have.has(d.id) && conditionMet(d.condition, s, counters)).map((d) => d.id);
}
